import { useQuery } from "@tanstack/react-query";
import { getWebhookLogs } from "@/services/call.service";

export interface WebhookLogEntry {
  event: string;
  url?: string | null;
  response_code: number | null;
  success: boolean | null;
  source: string | null;
  sent_at: string | null;
  response_body?: string | null;
  error?: string | null;
  payload_bytes?: number;
  payload_summary?: Record<string, unknown> | null;
  note?: string | null;
}

export type WebhookAttemptOutcome = "sending" | "2xx" | "http_error" | "redirect" | "timeout" | "network" | "too_large";

/**
 * One POST as `webhook_attempts` recorded it, newest first. A `member` gets the
 * metadata only; the optional fields come to developer+.
 */
export interface WebhookAttempt {
  event: string;
  attempt_no: number;
  source: string | null;
  started_at: string;
  outcome: WebhookAttemptOutcome;
  response_status: number | null;
  duration_ms: number | null;
  url_host: string | null;
  id?: string;
  url?: string;
  request_method?: string;
  request_headers?: Record<string, string> | null;
  /** Null once the call's content was scrubbed. */
  request_body?: string | null;
  body_bytes?: number;
  response_headers?: Record<string, string> | null;
  response_body?: string | null;
  finished_at?: string | null;
  error_message?: string | null;
  lost_lease?: boolean;
}

export interface WebhookLogsResult {
  /** The legacy log (`call_logs.webhook_event_logs`): what calls from before `webhook_attempts` show. */
  logs: WebhookLogEntry[];
  attempts?: WebhookAttempt[];
  role: string;
  can_retry: boolean;
  webhook_configured: boolean;
}

/**
 * Fetch a call's webhook delivery log via the `call` function, which redacts it to the role
 * (the raw column and `webhook_attempts` are closed to client roles). Enabled
 * only while the detail dialog / Webhooks tab is open.
 */
export function useWebhookEventLogs(callId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["webhook-event-logs", callId],
    enabled: Boolean(callId) && enabled,
    staleTime: 10_000,
    queryFn: () => getWebhookLogs(callId!),
  });
}
